import { useState, useEffect, useRef } from "react";
import { Drawer, Box, IconButton, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import LocationPermissionModal from "../../../../components/PermissionModal";
import save from "../../../../assets/save.svg";
import { decryptToken } from "../../../../Utlis/Encryption.js";
import { BASE_URL } from "../../../../components/BaseUrl.js";
import MultiSelectDropdown from "../../../../components/MultiSelectionDropdown";
import SearchInput from "../../../../components/SearchInput";
import {
  GoogleMap,
  Marker,
  Circle,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";
import { MAPS_API_KEY } from "../../../../components/mapKey";
import RoomIcon from "@mui/icons-material/Room";
import crossmap from "../../../../assets/crossmap.svg";
import Webcam from "react-webcam";
import { Menu, MenuItem } from "@mui/material";
import { convertLocalDateTimeToUTC } from "../../../../components/dateTimeUtils";
import {
  getPositionWithFallback,
  useLocationPermissionSync,
} from "../../../../utils/getPositionWithFallback";

const WorkerAddTask = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

     const { isLoaded } = useJsApiLoader({
  googleMapsApiKey: MAPS_API_KEY,
  libraries: ["places"],
});


  const [loading, setLoading] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showFullMap, setShowFullMap] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const fileInputRef = useRef(null);
  const autocompleteRef = useRef(null);

  // Camera position for an empty map only. Never used as a marker or saved value.
  const defaultCenter = { lat: 33.6844, lng: 73.0479 };
  
const [docAnchorEl, setDocAnchorEl] = useState(null);
const openDocMenu = Boolean(docAnchorEl);


const [showDocCamera, setShowDocCamera] = useState(false);
const docWebcamRef = useRef(null);
const [capturedDoc, setCapturedDoc] = useState(null);
const [takingPhoto, setTakingPhoto] = useState(false);
  const [formValues, setFormValues] = useState({
    title: "",
    description: "",
    priority: "",
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    attachments: [],
    face_required: true,
    location_required: true,
    evidence_required: false,
    radius_m: 100,
    location: {
      address: "",
      lat: null,
      lng: null,
    },
  });

  // Get current location when drawer opens
  useEffect(() => {
    if (open && token) {
      getCurrentLocation();
    }
  }, [open, token]);

  // Handle online/offline events for internet connectivity
  useEffect(() => {
    const handleOnline = () => {
      toast.success(t("internetRestored") || "Internet connection restored");
      // Try fetching address again if we already have coordinates
      if (formValues.location.lat && formValues.location.lng) {
        fetchAddressFromCoords(formValues.location);
      }
    };

    window.addEventListener("online", handleOnline);

    return () => {
      window.removeEventListener("online", handleOnline);
    };
  }, [formValues.location, t]);

  // Get current location when drawer opens
  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported");
      return;
    }

    if (!navigator.onLine) {
      toast.error(t("locationInternetError") || "Internet connection required");
      return;
    }

    setLocationLoading(true);
    getPositionWithFallback(
      (position) => {
        const coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        console.log("📍 Current location:", coords);
        setCurrentLocation(coords);
        
        // Set as initial location if not already set
        if (!formValues.location.lat || !formValues.location.lng) {
          setFormValues((prev) => ({
            ...prev,
            location: {
              address: "",
              lat: coords.lat,
              lng: coords.lng,
            },
          }));
          fetchAddressFromCoords(coords);
        }
        setLocationLoading(false);
      },
      (error) => {
        console.error("❌ Error getting location:", error);
        if (error.code === error.PERMISSION_DENIED) {
          // Denied: show the permission modal
          setShowPermissionModal(true);
        } else {
          toast.error(
            t("locationFetchFailed") ||
              "Unable to fetch your location. Please make sure Location Services are on and try again."
          );
        }
        // Never invent a location: leave it empty so the user picks one
        setLocationLoading(false);
      }
    );
  };

  useLocationPermissionSync({
    enabled: open,
    onGranted: () => {
      if (showPermissionModal || !currentLocation) {
        setShowPermissionModal(false);
        getCurrentLocation();
      }
    },
    onDenied: () => setShowPermissionModal(true),
  });

  // Fetch address from coordinates
const fetchAddressFromCoords = ({ lat, lng }) => {
  if (!window.google) return;

  const geocoder = new window.google.maps.Geocoder();

  geocoder.geocode({ location: { lat, lng } }, (results, status) => {
    if (status === "OK" && results[0]) {
      const formattedAddress = results[0].formatted_address;

      setSelectedLocation({ address: formattedAddress, lat, lng });

      setFormValues((prev) => ({
        ...prev,
        location: {
          address: formattedAddress,
          lat,
          lng,
        },
      }));
    } else {
      console.error("Geocoder failed:", status);
    }
  });
};


  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    if (type === "checkbox") {
      setFormValues((prev) => ({ ...prev, [name]: checked }));
    } else if (type === "file") {
      if (files && files.length > 0) {
        const fileArray = Array.from(files);
        setFormValues((prev) => ({
          ...prev,
          attachments: [...prev.attachments, ...fileArray],
        }));
      }
    } else {
      setFormValues((prev) => ({ ...prev, [name]: value }));
    }
  };

  // Handle place selection from autocomplete
  const handlePlaceSelect = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      
      if (place.geometry && place.geometry.location) {
        const coords = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        };
        
        setCurrentLocation(coords);
        fetchAddressFromCoords(coords);
      } else {
        toast.error("No details available for this place");
      }
    }
  };

const handleLocationSearch = (query) => {
  if (!window.google || query.length < 3) return;

  const service = new window.google.maps.places.AutocompleteService();

  service.getPlacePredictions(
    { input: query },
    (predictions, status) => {
      if (
        status === window.google.maps.places.PlacesServiceStatus.OK &&
        predictions.length > 0
      ) {
        const placeId = predictions[0].place_id;

        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ placeId }, (results, geoStatus) => {
          if (geoStatus === "OK" && results[0]) {
            const location = results[0].geometry.location;

            const coords = {
              address: results[0].formatted_address,
              lat: location.lat(),
              lng: location.lng(),
            };

            setSelectedLocation(coords);
            setCurrentLocation(coords);

            setFormValues((prev) => ({
              ...prev,
              location: coords,
            }));
          }
        });
      }
    }
  );
};

  const removeFile = (index) => {
    setFormValues((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

 const uploadFiles = async () => {
  if (formValues.attachments.length === 0) return [];

  setUploadingFiles(true);
  const uploadedUrls = [];

  try {
    for (const file of formValues.attachments) {
      const formData = new FormData();

      const isPdf =
        file.type === "application/pdf" ||
        file.name.toLowerCase().endsWith(".pdf");

      const uploadUrl = isPdf
        ? `${BASE_URL}/upload/pdf`
        : `${BASE_URL}/upload/image`;

      // ✅ IMPORTANT: correct key
      if (isPdf) {
        formData.append("pdf", file);
      } else {
        formData.append("image", file);
      }

      console.log("⬆️ Uploading file:", {
        name: file.name,
        type: file.type,
        size: file.size,
        endpoint: uploadUrl,
      });

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      console.log("📥 Upload response status:", res.status);

      const data = await res.json();
      console.log("📥 Upload response body:", data);

      if (!res.ok) {
        throw new Error(data?.message || "Upload failed");
      }

      const uploadedUrl = data?.data?.url || data?.url || data?.path;

      if (!uploadedUrl) {
        throw new Error("Upload succeeded but no URL returned");
      }

      uploadedUrls.push(uploadedUrl);
    }
  } catch (err) {
    console.error("❌ File upload error (detailed):", err);
    throw err;
  } finally {
    setUploadingFiles(false);
  }

  return uploadedUrls;
};

  const handleSave = async () => {
    // Validation
    if (!formValues.title.trim()) {
      toast.error(t("taskCreate.titleRequired") || "Task title is required");
      return;
    }
    if (!formValues.priority) {
      toast.error(t("taskCreate.priorityRequired") || "Priority is required");
      return;
    }
    if (!formValues.startDate || !formValues.startTime) {
      toast.error(t("taskCreate.startDateTimeRequired") || "Start date and time are required");
      return;
    }
    if (!formValues.endDate || !formValues.endTime) {
      toast.error(t("taskCreate.endDateTimeRequired") || "End date and time are required");
      return;
    }
    // Location validation bypassed — face/location restrictions always enabled on save
    // if (formValues.location_required && (!formValues.location.lat || !formValues.location.lng)) {
    //   toast.error(t("taskCreate.locationRequired") || "Location is required");
    //   return;
    // }

    // Validate date/time logic
    const startDateTime = new Date(`${formValues.startDate}T${formValues.startTime}`);
    const endDateTime = new Date(`${formValues.endDate}T${formValues.endTime}`);

    if (endDateTime <= startDateTime) {
      toast.error(t("taskCreate.endAfterStart") || "End date/time must be after start date/time");
      return;
    }

    try {
      setLoading(true);

      // Upload files first to get URLs
      let supportingProofUrls = [];
      if (formValues.attachments.length > 0) {
        supportingProofUrls = await uploadFiles();
      }

      // Create Task API payload (POST /task-management/worker/tasks)
      // {
      //   title: string,
      //   description: string,
      //   priority: "low" | "medium" | "high" | "urgent",
      //   start_at: ISODateString,
      //   end_at: ISODateString,
      //   assignees: number[],              // [] -> auto assign to self
      //   supporting_proof: string[],       // uploaded file URLs
      //   face_required: boolean,
      //   location_required: boolean,
      //   evidence_required: boolean,
      //   completion_policy: "all",
      //   meta: {
      //     timezone: string,
      //     allow_decline: boolean,
      //     allow_reschedule: boolean
      //   },
      //   location?: {
      //     address: string,
      //     lat: number,
      //     lng: number,
      //     radius_m: number
      //   }
      // }
      const utcStartAt = convertLocalDateTimeToUTC(
        `${formValues.startDate} ${formValues.startTime}`
      );
      const utcEndAt = convertLocalDateTimeToUTC(
        `${formValues.endDate} ${formValues.endTime}`
      );

      if (!utcStartAt || !utcEndAt) {
        toast.error(t("taskCreate.error") || "Error creating task");
        return;
      }

      const requestBody = {
        title: formValues.title.trim(),
        description: formValues.description.trim() || "",
        priority: formValues.priority,
        start_at: utcStartAt,
        end_at: utcEndAt,
        assignees: [], // Empty array = auto-assign to self (worker creating the task)
        supporting_proof: supportingProofUrls, // Array of uploaded file URLs
        face_required: true,
        location_required: true,
        evidence_required: formValues.evidence_required,
        completion_policy: "all",
        meta: {
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          allow_decline: true,
          allow_reschedule: true,
        },
      };

      // Add location when coordinates are available (location_required always true)
      if (formValues.location.lat && formValues.location.lng) {
        requestBody.location = {
          address: formValues.location.address || "",
          lat: formValues.location.lat,
          lng: formValues.location.lng,
          radius_m: parseInt(formValues.radius_m) || 100,
        };
      }

      console.log("📤 Creating task:", requestBody);

      const res = await fetch(`${BASE_URL}/task-management/worker/tasks`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await res.json();

      if (!data.error) {
        toast.success(data.message || t("taskCreate.success") || "Task created successfully!");
        if (onSuccess) onSuccess();
        
        // Reset form
        setFormValues({
          title: "",
          description: "",
          priority: "",
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
          assignees: [],
          attachments: [],
          face_required: true,
          location_required: true,
          evidence_required: false,
          radius_m: 100,
          location: { address: "", lat: null, lng: null },
        });
        setSelectedLocation(null);
        onClose();
      } else {
        toast.error(data.message || t("taskCreate.failed") || "Failed to create task");
      }
    } catch (err) {
      console.error("❌ Create task error:", err);
      toast.error(t("taskCreate.error") || "Error creating task");
    } finally {
      setLoading(false);
    }
  };

  const priorityOptions = [
    t("taskCreate.priorityLow") || "Low",
    t("taskCreate.priorityMedium") || "Medium",
    t("taskCreate.priorityHigh") || "High",
    t("taskCreate.priorityUrgent") || "Urgent",
  ];
  
  const priorityValueMap = {
    [t("taskCreate.priorityLow") || "Low"]: "low",
    [t("taskCreate.priorityMedium") || "Medium"]: "medium",
    [t("taskCreate.priorityHigh") || "High"]: "high",
    [t("taskCreate.priorityUrgent") || "Urgent"]: "urgent",
  };


    const startRef = useRef(null);
  const endRef = useRef(null);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 550, md: 600 },
          maxWidth: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "#E7EBEE",
          p: 1.3,
          borderBottom: "1px solid #E0E0E0",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.2 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            }}
          >
            {t("taskCreate.title") || "Create Task"}
          </CustomText>
        </Box>
      </Box>

      <Box sx={{ p: 2, overflowY: "auto", flexGrow: 1 }}>
        {/* Task Name */}
        <Box mb={2}>
          <label className="field_label">
            {t("taskCreate.taskName") || "Task Name"} <span className="red_asterisk">*</span>
          </label>
          <input
            name="title"
            className="project_input"
            style={{ width: "100%", height: 40, marginTop: 6 }}
            value={formValues.title}
            onChange={handleChange}
            placeholder={t("taskCreate.taskNamePlaceholder") || "Enter task name"}
          />
        </Box>

        {/* Priority */}
        <Box mb={2}>
          <label className="field_label">
            {t("taskCreate.priority") || "Priority"} <span className="red_asterisk">*</span>
          </label>
          <MultiSelectDropdown
            name="priority"
            values={formValues.priority ? [Object.keys(priorityValueMap).find(key => priorityValueMap[key] === formValues.priority) || formValues.priority] : []}
            onChange={(e) => {
              const selectedLabel = e.target.value[0] || "";
              const selectedValue = priorityValueMap[selectedLabel] || selectedLabel;
              setFormValues((prev) => ({ ...prev, priority: selectedValue }));
            }}
            options={priorityOptions}
          />
        </Box>

        {/* Starting Date & Time */}
        <Box display="flex" gap={1.5} mb={2}>
          <Box sx={{ flex: 1 }}>
            <label className="field_label">
              {t("taskCreate.startingDate") || "Starting Date"} <span className="red_asterisk">*</span>
            </label>
            <input
               ref={startRef}
    onClick={() => startRef.current?.showPicker()}
              name="startDate"
              type="date"
              className="project_input"
              style={{ width: "100%", height: 40, marginTop: 6 }}
              value={formValues.startDate}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <label className="field_label">
              {t("taskCreate.startingTime") || "Starting Time"} <span className="red_asterisk">*</span>
            </label>
            <input
              name="startTime"
              type="time"
              className="project_input"
              style={{ width: "100%", height: 40, marginTop: 6 }}
              value={formValues.startTime}
              onChange={handleChange}
            />
          </Box>
        </Box>

        {/* Ending Date & Time */}
        <Box display="flex" gap={1.5} mb={2}>
          <Box sx={{ flex: 1 }}>
            <label className="field_label">
              {t("taskCreate.endingDate") || "Ending Date"} <span className="red_asterisk">*</span>
            </label>
            <input
               ref={endRef}
    onClick={() => endRef.current?.showPicker()}
              name="endDate"
              type="date"
              className="project_input"
              style={{ width: "100%", height: 40, marginTop: 6 }}
              value={formValues.endDate}
              onChange={handleChange}
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <label className="field_label">
              {t("taskCreate.endingTime") || "Ending Time"} <span className="red_asterisk">*</span>
            </label>
            <input
              name="endTime"
              type="time"
              className="project_input"
              style={{ width: "100%", height: 40, marginTop: 6 }}
              value={formValues.endTime}
              onChange={handleChange}
            />
          </Box>
        </Box>

        {/* Description */}
        <Box mb={2}>
          <label className="field_label">
            {t("taskCreate.description") || "Description"}
          </label>
          <textarea
            name="description"
            rows={4}
            className="project_input"
            style={{ width: "100%", marginTop: 6 }}
            value={formValues.description}
            onChange={handleChange}
            placeholder={t("taskCreate.descriptionPlaceholder") || "Enter task description"}
          />
        </Box>

        {/* Supporting Documents */}
        <Box mb={2}>
          <label className="field_label">
            {t("taskCreate.supportingDocument") || "Supporting Document"}
          </label>
          <Box
            sx={{
              border: "1px dashed #D0D5DD",
              borderRadius: 2,
              p: 2,
              textAlign: "center",
              backgroundColor: "#F9FAFB",
              cursor: "pointer",
              mt: 1,
              minHeight: 100,
            }}
           onClick={(e) => {
e.stopPropagation();
setDocAnchorEl(e.currentTarget);
}}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf"
              onChange={handleChange}
              hidden
            />
            <CustomText sx={{ fontSize: 14, fontWeight: 500, pt: 2 }}>
              📎 {t("taskCreate.dragFiles") || "Drag files here or click to browse"}
            </CustomText>
            <CustomText sx={{ fontSize: 12, color: "#666", mt: 1 }}>
              {t("taskCreate.onlyImagesPdf") || "Only Images or PDFs are allowed."}
            </CustomText>
          </Box>

          {/* Display uploaded files */}
          {formValues.attachments.length > 0 && (
            <Box sx={{ mt: 1 }}>
              {formValues.attachments.map((file, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1,
                    border: "1px solid #E4E6EF",
                    borderRadius: 1,
                    mb: 0.5,
                  }}
                >
                  <CustomText sx={{ fontSize: 12 }}>{file.name}</CustomText>
                  <IconButton size="small" onClick={() => removeFile(index)}>
                    <img src={profilecross} alt="remove" style={{ width: 16, height: 16 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>
<Menu
  anchorEl={docAnchorEl}
  open={openDocMenu}
  onClose={() => setDocAnchorEl(null)}
>
  <MenuItem
    onClick={() => {
      setDocAnchorEl(null);
      setCapturedDoc(null);
      setShowDocCamera(true);
    }}
    sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}
  >
    {t("takePhoto")}
  </MenuItem>

  <MenuItem
    onClick={() => {
      setDocAnchorEl(null);
      fileInputRef.current?.click();
    }}
    sx={{ fontSize: "13px", fontFamily: 'Poppins, sans-serif' }}
  >
    {t("chooseFromGallery")}
  </MenuItem>
</Menu>
        {/* Face scan & location restriction toggles — always sent as true on save */}
        {/* <Box mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={formValues.face_required}
                onChange={(e) => setFormValues((prev) => ({ ...prev, face_required: e.target.checked }))}
                name="face_required"
              />
            }
            label={t("taskCreate.faceScanRestriction") || "Face Scan Restriction"}
              componentsProps={{
    typography: {
      sx: { fontSize: "16px" }
    }
  }}
          />
        </Box>

        <Box mb={2}>
          <FormControlLabel
            control={
              <Switch
                checked={formValues.location_required}
                onChange={(e) => setFormValues((prev) => ({ ... prev, location_required: e.target.checked }))}
                name="location_required"
              />
            }
              componentsProps={{
    typography: {
      sx: { fontSize: "16px" }
    }
  }}
            label={t("taskCreate.locationRestriction") || "Location Restriction"}
          />
        </Box> */}

        {/* Radius (location always required) */}
        <Box mb={2}>
          <label className="field_label">
            {t("taskCreate.radius") || "Radius (meters)"}
          </label>
          <input
            name="radius_m"
            type="number"
            className="project_input"
            style={{ width: "100%", height: 40, marginTop: 6 }}
            value={formValues.radius_m}
            onChange={handleChange}
            min="1"
          />
        </Box>

        {/* Pick from Live location */}
        <Box mb={2}>
            <label className="field_label">
              {t("taskCreate.pickFromLiveLocation") || "Pick from Live location"} <span className="red_asterisk">*</span>
            </label>
            
            {/* Search and Current Location Button */}
            <Box sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}>
              <Box sx={{ flex: 1 }}>
                {isLoaded ? (
                  <Autocomplete
                    onLoad={(autocomplete) => {
                      autocompleteRef.current = autocomplete;
                    }}
                    onPlaceChanged={handlePlaceSelect}
                  >
                    <input
                      type="text"
                      placeholder={t("taskCreate.searchLocation") || "Search location..."}
                      value={formValues.location.address || ""}
                      onChange={(e) => {
                        const query = e.target.value;
                        setFormValues((prev) => ({
                          ...prev,
                          location: { ...prev.location, address: query },
                        }));
                      }}
                      style={{
                        width: "100%",
                        height: 40,
                        padding: "0 12px",
                        fontSize: "14px",
                        border: "1px solid #D0D5DD",
                        borderRadius: "8px",
                        fontFamily: "Poppins, sans-serif",
                        outline: "none",
                      }}
                    />
                  </Autocomplete>
                ) : (
                  <input
                    type="text"
                    placeholder="Loading..."
                    disabled
                    style={{
                      width: "100%",
                      height: 40,
                      padding: "0 12px",
                      fontSize: "14px",
                      border: "1px solid #D0D5DD",
                      borderRadius: "8px",
                      fontFamily: "Poppins, sans-serif",
                      backgroundColor: "#f5f5f5",
                    }}
                  />
                )}
              </Box>
              <IconButton
                onClick={getCurrentLocation}
                disabled={locationLoading}
                sx={{
                  border: "1px solid #C4C4C4",
                  borderRadius: 1,
                  width: 40,
                  height: 40,
                  backgroundColor: locationLoading ? "#f5f5f5" : "white",
                }}
                title={t("taskCreate.getCurrentLocation") || "Get Current Location"}
              >
                {locationLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  <RoomIcon sx={{ color: "#006EC2" }} />
                )}
              </IconButton>
              <CustomButton
                label={t("taskCreate.fullMap") || "Full Map"}
                size="small"
                onClick={() => setShowFullMap(!showFullMap)}
              />
            </Box>

            {/* Map preview with current location */}
            <Box
              sx={{
                height: 250,
                width: "100%",
                borderRadius: 1,
                overflow: "hidden",
                mt: 1,
                border: "1px solid #E4E6EF",
                position: "relative",
              }}
            >
              {locationLoading && !formValues.location.lat ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  <CircularProgress />
                  <CustomText sx={{ ml: 2 }}>
                    {t("taskCreate.gettingLocation") || "Getting your location..."}
                  </CustomText>
                </Box>
              ) : (
             
                <Box
  sx={{
    height: 250,
    width: "100%",
    borderRadius: 1,
    overflow: "hidden",
    mt: 1,
    border: "1px solid #E4E6EF",
  }}
>
  {!isLoaded ? (
    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
      <CircularProgress />
    </Box>
  ) : (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "100%" }}
      center={
        formValues.location.lat && formValues.location.lng
          ? { lat: formValues.location.lat, lng: formValues.location.lng }
          : currentLocation || defaultCenter
      }
      zoom={15}
    >
      {formValues.location.lat && formValues.location.lng && (
        <>
          <Marker
            position={{
              lat: formValues.location.lat,
              lng: formValues.location.lng,
            }}
            draggable
            onDragEnd={(e) => {
              const newPos = {
                lat: e.latLng.lat(),
                lng: e.latLng.lng(),
              };
              setCurrentLocation(newPos);
              fetchAddressFromCoords(newPos);
            }}
          />

          <Circle
            center={{
              lat: formValues.location.lat,
              lng: formValues.location.lng,
            }}
            radius={parseInt(formValues.radius_m) || 100}
            options={{
              fillColor: "#006EC2",
              fillOpacity: 0.2,
              strokeColor: "#006EC2",
            }}
          />
        </>
      )}
    </GoogleMap>
  )}
</Box>
              )}
            </Box>

            {/* Display selected address */}
            {formValues.location.address && (
              <Box
                sx={{
                  mt: 1,
                  p: 1.5,
                  backgroundColor: "#F9FAFB",
                  borderRadius: 1,
                  border: "1px solid #E4E6EF",
                }}
              >
                <CustomText sx={{ fontSize: 12, color: "#666", mb: 0.5 }}>
                  {t("taskCreate.selectedAddress") || "Selected Address:"}
                </CustomText>
                <CustomText sx={{ fontSize: 14, color: "#172B4D" }}>
                  {formValues.location.address}
                </CustomText>
              </Box>
            )}

            {/* Full map modal */}
            {showFullMap && (
              <Box
                sx={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  width: "100vw",
                  height: "100vh",
                  backgroundColor: "white",
                  zIndex: 2000,
                }}
              >
                <Box
                  sx={{
                    position: "absolute",
                    top: 16,
                    right: 16,
                    zIndex: 2100,
                  }}
                >
                  <IconButton
                    onClick={() => setShowFullMap(false)}
                    sx={{
                      backgroundColor: "white",
                      "&:hover": { backgroundColor: "#f5f5f5" },
                    }}
                  >
                    <img src={crossmap} alt="close" style={{ width: 50, height: 50 }} />
                  </IconButton>
                </Box>

                {!isLoaded ? (
                  <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <GoogleMap
                    mapContainerStyle={{ width: "100%", height: "100%" }}
                    center={
                      formValues.location.lat && formValues.location.lng
                        ? { lat: formValues.location.lat, lng: formValues.location.lng }
                        : currentLocation || defaultCenter
                    }
                    zoom={15}
                  >
                    {formValues.location.lat && formValues.location.lng && (
                      <>
                        <Marker
                          position={{
                            lat: formValues.location.lat,
                            lng: formValues.location.lng,
                          }}
                          draggable
                          onDragEnd={(e) => {
                            const newPos = {
                              lat: e.latLng.lat(),
                              lng: e.latLng.lng(),
                            };
                            setCurrentLocation(newPos);
                            fetchAddressFromCoords(newPos);
                          }}
                        />

                        <Circle
                          center={{
                            lat: formValues.location.lat,
                            lng: formValues.location.lng,
                          }}
                          radius={parseInt(formValues.radius_m) || 100}
                          options={{
                            fillColor: "#006EC2",
                            fillOpacity: 0.2,
                            strokeColor: "#006EC2",
                          }}
                        />
                      </>
                    )}
                  </GoogleMap>
                )}
              </Box>
            )}
          </Box>

        {/* Save Button */}
        <Box sx={{ mt: 3 }}>
          <CustomButton
            onClick={handleSave}
            disabled={loading || uploadingFiles}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {(loading || uploadingFiles) && (
                  <CircularProgress size={18} thickness={4} sx={{ color: "#ffffff" }} />
                )}
                <img src={save} alt="save" style={{ width: 16, height: 16 }} />
                {uploadingFiles
                  ? t("taskCreate.uploading") || "Uploading..."
                  : t("taskCreate.create") || "Create Task"}
              </Box>
            }
            size="medium"
            fullWidth
          />
        </Box>
      </Box>
      {showDocCamera && (
  <Box
    sx={{
      position: "fixed",
      inset: 0,
      backgroundColor: "rgba(0,0,0,0.6)",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      zIndex: 1300,
      p: 2,
    }}
  >
    <Box
      sx={{
        backgroundColor: "#fff",
        borderRadius: 2,
        p: 3,
        width: "100%",
        maxWidth: 500,
      }}
    >
      {!capturedDoc ? (
        <Webcam
          ref={docWebcamRef}
          screenshotFormat="image/jpeg"
          videoConstraints={{ facingMode: "environment" }}
          style={{ width: "100%", borderRadius: 8 }}
        />
      ) : (
        <img
          src={capturedDoc}
          alt="Captured"
          style={{ width: "100%", borderRadius: 8 }}
        />
      )}

      <Box sx={{ display: "flex", justifyContent:"center",gap:2,  mt: 2 }}>
        {!capturedDoc ? (
          <CustomButton
            label={takingPhoto ? t("taskCreate.uploading") : t("capture")}
            disabled={takingPhoto}
            onClick={async () => {
              const img = docWebcamRef.current?.getScreenshot();
              if (!img) return;

              setTakingPhoto(true);
              setCapturedDoc(img);

              const blob = await fetch(img).then((r) => r.blob());
              const file = new File([blob], "document.jpg", {
                type: "image/jpeg",
              });

              // 👇 ADD to existing attachments
              setFormValues((prev) => ({
                ...prev,
                attachments: [...prev.attachments, file],
              }));

              setTakingPhoto(false);
              setShowDocCamera(false);
            }}
          />
        ) : (
          <CustomButton
            label={t("retake")}
            onClick={() => setCapturedDoc(null)}
          />
        )}

        <CustomButton
          label={t("close")}
          onClick={() => setShowDocCamera(false)}
        />
      </Box>
    </Box>
  </Box>
)}

      {/* Permission Modal */}
      <LocationPermissionModal
        open={showPermissionModal}
        onAllow={() => {
          setShowPermissionModal(false);
          getCurrentLocation();
        }}
        onCancel={() => setShowPermissionModal(false)}
      />
    </Drawer>
  );
};

export default WorkerAddTask;
